import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, ShieldCheck } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/Button';
import { Input, Field } from '@/components/ui/Input';
import { useAuth } from '@/lib/auth';
import { useBranding } from '@/lib/branding';
import { useToast } from '@/components/ui/Toast';
import GoogleAuthButton from '@/components/GoogleAuthButton';

const HERO_IMG = 'https://static.prod-images.emergentagent.com/jobs/6170d9f7-0af6-44a4-9bd0-8b49a78661d1/images/734f0c5408bbc36fd5d93660b9e42d2c03d2e308d6896cb0466df742d64a70a4.png';

export function AuthSplitLayout({ children, mode = 'customer' }) {
  const { app_name } = useBranding();
  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-obsidian text-white">
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden grain">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-obsidian via-obsidian/80 to-obsidian/40" />
        <div className="relative z-10">
          <Logo />
        </div>
        <div className="relative z-10 max-w-md">
          {mode === 'admin' ? (
            <>
              <ShieldCheck className="text-volt mb-6" size={36} />
              <h2 className="font-heading font-black text-5xl uppercase leading-none tracking-tighter">
                Command<br />the <span className="text-volt">academy.</span>
              </h2>
              <p className="mt-6 text-white/60">Control bookings, memberships, courts, payments, and audits — all from one place.</p>
            </>
          ) : (
            <>
              <h2 className="font-heading font-black text-5xl uppercase leading-none tracking-tighter">
                Welcome<br />back, <span className="text-volt">champion.</span>
              </h2>
              <p className="mt-6 text-white/60">Log in to book courts, track bookings, and unlock member-only perks.</p>
            </>
          )}
        </div>
        <div className="relative z-10 text-[10px] uppercase tracking-[0.25em] text-white/30">
          {app_name} · by Kalgyan Solutions
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">{children}</div>
      </div>
    </div>
  );
}

export default function Login() {
  const [email, setEmail] = useState();
  const [password, setPassword] = useState();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // NEW: email verification. A 403/EMAIL_NOT_VERIFIED from the backend means
  // the account exists but hasn't clicked its verification link yet — offer
  // a resend instead of just showing a generic "login failed".
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resending, setResending] = useState(false);
  const { login, resendVerification } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setNeedsVerification(false);
    try {
      const u = await login(email, password);
      toast({ title: 'Welcome back', description: `Logged in as ${u.name}`, variant: 'success' });
      if (['operator', 'super_admin', 'admin'].includes(u.role)) {
        navigate('/admin');
      } else {
        navigate('/app/bookings');
      }
    } catch (err) {
      if (err.response?.data?.detail === 'EMAIL_NOT_VERIFIED') {
        setNeedsVerification(true);
        setError('Please verify your email before logging in.');
      } else {
        setError(err.response?.data?.detail || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await resendVerification(email);
      toast({ title: 'Verification email sent', description: email, variant: 'success' });
    } catch (err) {
      toast({ title: 'Could not resend right now', description: err.response?.data?.detail || 'Try again shortly', variant: 'error' });
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthSplitLayout mode="customer">
      <div className="lg:hidden mb-10"><Logo /></div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-volt mb-3">Member Sign In</div>
      <h1 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tight leading-none">
        Log in to your<br />court life.
      </h1>
      <p className="mt-3 text-white/50 text-sm">Don't have an account? <Link to="/register" className="text-volt hover:underline" data-testid="goto-register">Create one</Link>.</p>

      <form onSubmit={handleSubmit} className="mt-10 space-y-5" data-testid="login-form">
        <Field label="Email" htmlFor="email" error={error}>
          <Input
            id="email"
            type="email"
            data-testid="login-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
          />
        </Field>
        <Field label="Password" htmlFor="password">
          <div className="relative">
            <Input
              id="password"
              type={show ? 'text' : 'password'}
              data-testid="login-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              className="pr-12"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-volt"
              aria-label="toggle password"
            >
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </Field>

        {needsVerification && (
          <button
            type="button"
            onClick={handleResend}
            disabled={resending}
            className="text-xs text-volt hover:underline"
            data-testid="resend-verification-from-login"
          >
            {resending ? 'Sending…' : 'Resend verification email'}
          </button>
        )}

        <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
          {loading ? 'Signing in…' : <>Sign In <ArrowRight size={16} /></>}
        </Button>

        <Link
  to="/forgot-password"
  className="text-sm text-volt block text-right "
>
  Forgot Password?
</Link>

        <div className="mt-6">
  <div className="relative mb-4">
    <div className="absolute inset-0 flex items-center">
      <div className="w-full border-t border-white/10"></div>
    </div>

    <div className="relative flex justify-center text-xs uppercase">
      <span className="bg-obsidian px-2 text-white/50">
        Or
      </span>
    </div>
  </div>

  <GoogleAuthButton />
</div>

        <div className="text-center pt-2">
          <Link to="/" className="text-[10px] uppercase tracking-[0.25em] text-white/40 hover:text-volt">← Back to home</Link>
        </div>
      </form>
    </AuthSplitLayout>
  );
}
